import React from 'react'

import { Button, Popover, PopoverTrigger, getExtendedDialogState, usePopoverState } from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import ChartLayout from '../../models/chart_layout'
import { isRedesignEnabled } from '../../utils'
import { encodeQueryString } from '../../utils/query_string'
import { ChartSettings, PreventClosingUnsavedChanges } from '../chart-settings'

interface Props {
  isPremium: boolean
  model: ChartLayout
}

const BUTTON_POPOVER_GUTTER = 16
const SETTINGS_POPOVER_ID = 'settings-popover'

const utmCampaign: Partial<Record<SpecificChartFunctionality, string>> = {
  [SpecificChartFunctionality.quotePage]: 'quote-settings-button',
  [SpecificChartFunctionality.futuresPage]: 'futures-settings-button',
  [SpecificChartFunctionality.forexPage]: 'forex-settings-button',
  [SpecificChartFunctionality.cryptoPage]: 'crypto-settings-button',
}

type PopoverStylesType = { body?: React.CSSProperties; wrapper?: React.CSSProperties }

export function ChartSettingsButton({ isPremium, model, children }: React.PropsWithChildren<Props>) {
  const settingsPopoverState = usePopoverState({
    placement: 'bottom-start',
  })
  const { isFullyClosed } = getExtendedDialogState(settingsPopoverState)
  const chartElement = document.getElementById('chart')
  const popoverContentElement = document.getElementById(SETTINGS_POPOVER_ID)
  const settingsButton = settingsPopoverState.unstable_referenceRef.current

  const popoverStyles = React.useMemo(() => {
    const newPopoverStyles: PopoverStylesType = {}
    if (!settingsButton || !chartElement) {
      return newPopoverStyles
    }
    const settingsButtonBoundingClientRect = settingsButton.getBoundingClientRect()
    const topOffset = settingsButtonBoundingClientRect.top + settingsButtonBoundingClientRect.height
    newPopoverStyles.body = {
      maxHeight: window.innerHeight - topOffset - BUTTON_POPOVER_GUTTER * 2,
      position: 'sticky',
      top: -topOffset + BUTTON_POPOVER_GUTTER,
    }
    if (!popoverContentElement) {
      return newPopoverStyles
    }
    const chartHeight = chartElement.clientHeight
    const popoverContentHeight = popoverContentElement.offsetHeight
    const isRedesign = isRedesignEnabled()
    newPopoverStyles.wrapper = {
      height: popoverContentHeight < chartHeight ? chartHeight : undefined,
      ...(!isRedesign && {
        transform: `translate(${BUTTON_POPOVER_GUTTER}px, ${topOffset + BUTTON_POPOVER_GUTTER}px)`,
      }),
    }

    return newPopoverStyles
  }, [settingsButton, chartElement, popoverContentElement])

  const buttonProps = React.useMemo(() => {
    if (!isPremium) {
      return {
        as: 'a',
        href: `/elite.ashx?${encodeQueryString({
          utm_source: 'finviz',
          utm_medium: 'banner',
          utm_campaign: utmCampaign[model.specificChartFunctionality],
        })}`,
      }
    }

    return {
      disabled: !model,
      as: PopoverTrigger,
      state: settingsPopoverState,
      active: settingsPopoverState.visible,
    }
  }, [model, isPremium, settingsPopoverState])

  return (
    <>
      <Button
        leftContent="settingsWheel"
        theme="chip"
        size="medium"
        data-testid="chart-settings-open-btn"
        {...buttonProps}
      >
        {children}
      </Button>
      <PreventClosingUnsavedChanges onHide={settingsPopoverState.hide}>
        {({ checkForUnsavedChangesAndClose }) => (
          <Popover
            id={SETTINGS_POPOVER_ID}
            aria-label="Chart Settings"
            className="relative z-10 flex max-h-full w-145 max-w-full flex-col"
            state={settingsPopoverState}
            style={popoverStyles.body}
            popoverWrapperStyle={popoverStyles.wrapper}
            focusOnHide={false}
            hideOnClickOutside={false}
            onOverlayClick={checkForUnsavedChangesAndClose}
          >
            {!isFullyClosed && <ChartSettings onSubmit={() => {}} onClose={checkForUnsavedChangesAndClose} />}
          </Popover>
        )}
      </PreventClosingUnsavedChanges>
    </>
  )
}
